import * as React from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { store } from "../../utils/store";
import { selectPrivileges, selectRole1 } from "../../redux/authSlice";
import { canShowMenuItem } from "./menuPrivileges";

export default function AppSubmenu(props: {
  data: any;
  collapsed?: boolean;
  secondaryOpen?: boolean;
  onMenuClick?: (menu: any) => void;
}) {
  const collapsed = !!props.collapsed;
  const location = useLocation();
  const fullPath = location?.pathname || "";
  useSelector(selectPrivileges);
  const hierarchyLevel = String(
    useSelector(selectRole1) ?? store.getState().auth?.hierarchyLevel ?? ""
  );

  const normalizePath = (path: string) => {
    if (!path) return "";
    const cleaned = path.split("?")[0].replace(/\/+$/, "");
    return cleaned || "/";
  };

  const linkMatches = (link?: string) => {
    if (!link) return false;
    const target = normalizePath(link);
    const current = normalizePath(fullPath);
    return current === target || current.startsWith(`${target}/`);
  };

  const hasActiveChild = (item: any): boolean => {
    if (!item?.subMenu?.length) return false;
    return item.subMenu.some((menu: any) => {
      if (menu?.link && linkMatches(menu.link)) return true;
      return (menu?.subSubMenu || []).some((child: any) =>
        linkMatches(child?.link)
      );
    });
  };

  const isActive = props.data?.subMenu?.length
    ? hasActiveChild(props.data) || !!props.secondaryOpen
    : linkMatches(props.data?.link);

  const isAdminUser = hierarchyLevel === "1";
  const isAdminDashboardItem = props.data?.key === "ADMIN";
  const isMyDashboardItem = props.data?.key === "MY-Dashboard";

  if (isAdminDashboardItem && !isAdminUser) return null;
  if (isMyDashboardItem && isAdminUser) return null;

  const canShow =
    isAdminDashboardItem ||
    isMyDashboardItem ||
    props.data?.key === "Dashboard" ||
    canShowMenuItem(props.data);

  if (!canShow) return null;

  const label = props.data?.name || props.data?.text || props.data?.key;

  return (
    <li className="list-none">
      <button
        type="button"
        aria-label={label}
        aria-current={isActive ? "page" : undefined}
        aria-expanded={
          props.data?.subMenu?.length ? !!props.secondaryOpen : undefined
        }
        onClick={() => props.onMenuClick?.(props.data)}
        className={`relative flex w-full items-center rounded-lg outline-none transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${
          collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
        } ${
          isActive
            ? "bg-white text-[#841C39]"
            : "text-white/75 hover:bg-white/10 hover:text-white"
        }`}
      >
        {isActive ? (
          <span
            aria-hidden
            className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-[#841C39]"
          />
        ) : null}

        <span className="flex h-8 w-8 shrink-0 items-center justify-center">
          <img
            src={
              isActive
                ? props.data?.activeIcon || props.data?.icon
                : props.data?.icon
            }
            alt=""
            className="h-5 w-5 object-contain"
            style={
              isActive
                ? {
                    filter:
                      "brightness(0) saturate(100%) invert(14%) sepia(55%) saturate(2500%) hue-rotate(320deg) brightness(92%) contrast(95%)",
                  }
                : { filter: "brightness(0) invert(1)", opacity: 0.9 }
            }
          />
        </span>

        {!collapsed && (
          <span
            className={`min-w-0 flex-1 truncate text-left text-[13px] ${
              isActive ? "font-semibold text-[#841C39]" : "font-medium"
            }`}
          >
            {label}
          </span>
        )}
      </button>
    </li>
  );
}
